import * as CommandType from "./command-type";
import * as ResponseType from "./response-type";
import * as EventType from "./event-type";
import {
  ServerInfo,
  EventMessage,
  UploadType,
  DownloadInfo,
  ResponseParser,
} from "./model-type";
import { readBlobData, versionCompare } from "../../utils/conversion";
import { STORAGE } from "../../constants";
import { Cookie } from "../../utils";

const SIGN = "yunyu";
const DEFAULT_PASSWORD = "12345678";
const COMPATIBLE_VERSION = "3.0.3.8";
const END_SUPPORT_VERSION = "3.2.8.1";
const COMPATIBLE_SIGN = "yunyu";
const RAYSYNC_SIGN = "raysync";

const responseParser: ResponseParser = {
  [ResponseType.CHECK](res, result) {
    let success: boolean = true;
    if (
      res.sign.toLocaleLowerCase() === COMPATIBLE_SIGN &&
      versionCompare(res.ftpmanager_version, COMPATIBLE_VERSION) &&
      versionCompare(END_SUPPORT_VERSION, res.ftpmanager_version)
    ) {
      success = false;
    } else if (res.sign.toLocaleLowerCase() !== RAYSYNC_SIGN) {
      return {
        eventName: EventType.CONNECT_FAILED,
        success: false,
        data: {
          clientVersion: res.ftpmanager_version,
        },
      };
    }
    return {
      eventName: EventType.CHECK_SUCCESS,
      success,
      data: {
        clientVersion: res.ftpmanager_version,
      },
    };
  },
  [ResponseType.LOGIN](res, result) {
    if (0 === result) {
      return {
        eventName: EventType.HAS_LOGGED,
        success: true,
      };
    } else {
      return {
        eventName: EventType.HAS_LOGGED,
        success: false,
        code: result,
      };
    }
  },
  [ResponseType.LOGOUT](res, result) {
    return {
      eventName: EventType.LOGGED_OUT,
      success: 0 === result,
    };
  },
  [ResponseType.TASK_COUNT](res, result) {
    return {
      eventName: EventType.RECEIVE_TASK_COUNT,
      success: true,
      data: res,
    };
  },
  [ResponseType.UPDATE_CLIENT](res, result) {
    return {
      eventName: EventType.HAS_UPGRADE_VERSION,
      success: 0 === result,
    };
  },
  [ResponseType.GET_ERROR_INFO](res, result) {
    return {
      eventName: EventType.RECEIVE_ERROR_INFO,
      success: 0 === result,
      data: res,
    };
  },
  [ResponseType.DISCONNECT](res, result) {
    return {
      eventName: EventType.DISCONNECT,
      success: true,
      data: undefined,
    };
  },
  [ResponseType.UPLOAD](res, result) {
    return {
      eventName: EventType.UPLOAD,
      success: true,
      data: undefined,
    };
  },
};

export default class Protocol {
  private account: string;
  private token: string;
  private line: string;
  private platform: string;
  initUserInfo(account: string, token: string, line: string, platform: string) {
    this.account = account;
    this.token = token;
    this.line = line;
    this.platform = platform;
  }
  updateUserInfo(key: string, value: string) {
    this[key] = value;
  }
  async parseMessage(blob: Blob): Promise<EventMessage> {
    const str = await readBlobData(blob);
    const response = JSON.parse(str);
    const action: string = response.action;
    const result: number = response.result;
    if (Reflect.has(responseParser, action)) {
      return responseParser[action](response, result);
    } else {
      return {
        eventName: EventType.UNDEFINED,
        success: 0 === result,
        code: result,
      };
    }
  }
  [CommandType.CHECK](clientVersion: string, protocolVersion: string): string {
    return JSON.stringify({
      action: CommandType.CHECK,
      sign: SIGN,
      client_version: clientVersion,
      protocol_version: protocolVersion,
    });
  }
  [CommandType.LOGIN](serverInfo: ServerInfo): string {
    const lang =
      Cookie.getCookie(STORAGE.LANGUAGE_FLAG) === "0" ? "zh-CN" : "en";
    return JSON.stringify({
      action: CommandType.LOGIN,
      server_ip: serverInfo.host,
      server_port: Number(serverInfo.port),
      proxy_ip: serverInfo.proxyHost,
      proxy_port: Number(serverInfo.proxyPort),
      user_name: this.account,
      pass_word: `${this.token}|${DEFAULT_PASSWORD}`.replace(/\|/g, "&"),
      language: lang,
      server5_port: serverInfo.server5Port,
      server5_ssl_port: serverInfo.server5SSLPort,
      renderbus_proxy_port: serverInfo.renderbusProxyPort,
      tcpEnable: serverInfo.tcpEnable,
      tcpDelay: serverInfo.tcpDelay,
      web_server_ip: serverInfo.webServerIp,
      web_server_port: serverInfo.webServerPort,
      collectClientTransferWeb: `${window.location.origin}/api/rendering/task/transfer/collectClientTransferWeb`,
      autoUpdateSwitch: false,
    });
  }
  [CommandType.LS](directory: string): string {
    return JSON.stringify({
      action: CommandType.LS,
      path: directory,
    });
  }
  [CommandType.UPLOAD](
    type: UploadType,
    path: string,
    fullPath: boolean,
    renderbus_id: string
  ): string {
    return JSON.stringify({
      action: CommandType.UPLOAD,
      full_path: fullPath,
      type,
      path,
      interface_params: JSON.stringify({
        line: this.line,
        platform: this.platform,
        userName: this.account,
      }),
      renderbus_id: Number(renderbus_id),
    });
  }
  [CommandType.DOWNLOAD](
    src: DownloadInfo[],
    timestamp?: string,
    port?: number,
    serverIp?: string,
    exra = {}
  ): string {
    return JSON.stringify({
      action: CommandType.DOWNLOAD,
      src,
      timestamp,
      server_ip: serverIp,
      server_port: port,
      server5_port: port,
      pass_word: `${this.token}|${DEFAULT_PASSWORD}`.replace(/\|/g, "&"),
      interface_params: JSON.stringify({
        line: this.line,
        platform: this.platform,
        userName: this.account,
      }),
      ...exra,
    });
  }
  [CommandType.OPEN_TRANS_LIST](): string {
    return JSON.stringify({
      action: CommandType.OPEN_TRANS_LIST,
      x: 0,
      y: 0,
    });
  }
  [CommandType.START_UPDATE](version: string): string {
    return JSON.stringify({
      action: CommandType.START_UPDATE,
      version,
    });
  }
  [CommandType.GET_ERROR_INFO](): string {
    return JSON.stringify({
      action: CommandType.GET_ERROR_INFO,
    });
  }
  [CommandType.ACTIVE_WINDOW](active: boolean): string {
    return JSON.stringify({
      action: CommandType.ACTIVE_WINDOW,
      active,
    });
  }
  [CommandType.TIMING_TASK](path: string, rootPath: string): string {
    return JSON.stringify({
      action: CommandType.TIMING_TASK,
      path,
      group_id: 0,
      root_path: rootPath,
      forbid_sync_delete: true,
    });
  }
  [CommandType.UPDATE_ALL_NORMAL_TASK]({
    server5_port,
    server_ip,
    server_port,
    request_id = "",
    task_type = "",
    proxy_ip = "",
    proxy_port,
    renderbus_proxy_port,
    server5_ssl_port,
    renderbus_id,
  }: {
    server5_port: number;
    server_ip: string;
    server_port: number;
    request_id: string;
    task_type: string;
    proxy_ip: string;
    proxy_port: number;
    renderbus_proxy_port: number;
    server5_ssl_port: number;
    renderbus_id: string;
  }) {
    return JSON.stringify({
      action: CommandType.UPDATE_ALL_NORMAL_TASK,
      server5_port,
      server_ip,
      server_port,
      request_id,
      task_type,
      proxy_ip,
      proxy_port,
      renderbus_proxy_port,
      server5_ssl_port,
      renderbus_id: Number(renderbus_id),
    });
  }
}
